// The address plan as the forms edit it (text fields) and its browser checks (formats and ranges; the server checks
// the rules, spec 11 4.10).
import type { AttachmentSize, NetworkSettings } from '../../api/types';
import { strings } from '../../strings';
import { parseCidr } from './cidr';

const p = strings.networks.lists.plan;
const BASE = '/settings/addressPlan';

export interface AddressPlanForm {
  supernets: string[];
  /** The user changed the supernets (New network keeps its suggestion until then). */
  supernetsTouched: boolean;
  reserved: { cidr: string; label: string }[];
  regionPrefixLength: string;
  hubPrefixLength: string;
  defaultAttachmentSize: AttachmentSize;
}

export function planToForm(plan: NetworkSettings['addressPlan']): AddressPlanForm {
  return {
    supernets: [...plan.supernets],
    supernetsTouched: false,
    reserved: plan.reserved.map((r) => ({ ...r })),
    regionPrefixLength: String(plan.regionPrefixLength),
    hubPrefixLength: String(plan.hubPrefixLength),
    defaultAttachmentSize: plan.defaultAttachmentSize,
  };
}

export const DEFAULT_PLAN_FORM: AddressPlanForm = {
  supernets: [],
  supernetsTouched: false,
  reserved: [],
  regionPrefixLength: '16',
  hubPrefixLength: '22',
  defaultAttachmentSize: 'medium',
};

function toNumber(text: string, fallback: number): number {
  const n = Number(text);
  return text.trim() !== '' && Number.isInteger(n) ? n : fallback;
}

export function formToPlan(form: AddressPlanForm): NetworkSettings['addressPlan'] {
  return {
    supernets: form.supernets.map((s) => s.trim()).filter(Boolean),
    reserved: form.reserved.map((r) => ({ cidr: r.cidr.trim(), label: r.label.trim() })),
    regionPrefixLength: toNumber(form.regionPrefixLength, 16),
    hubPrefixLength: toNumber(form.hubPrefixLength, 22),
    defaultAttachmentSize: form.defaultAttachmentSize,
  };
}

/** Format and range checks in the browser, by JSON Pointer into the request body. */
export function checkPlanForm(form: AddressPlanForm): Record<string, string[]> {
  const errors: Record<string, string[]> = {};
  const add = (pointer: string, message: string) => (errors[pointer] ??= []).push(message);
  form.supernets.forEach((cidr, i) => {
    if (!parseCidr(cidr)) add(`${BASE}/supernets/${i}`, p.invalidCidr);
  });
  form.reserved.forEach((r, i) => {
    if (!parseCidr(r.cidr)) add(`${BASE}/reserved/${i}/cidr`, p.invalidCidr);
    if (!r.label.trim()) add(`${BASE}/reserved/${i}/label`, p.labelRequired);
  });
  const region = Number(form.regionPrefixLength);
  if (!Number.isInteger(region) || region < 12 || region > 20)
    add(`${BASE}/regionPrefixLength`, p.range(12, 20));
  const hub = Number(form.hubPrefixLength);
  if (!Number.isInteger(hub) || hub < 22 || hub > 26) add(`${BASE}/hubPrefixLength`, p.range(22, 26));
  return errors;
}

/** The messages at a pointer: the browser's and the field's diagnostics (prefix match). */
export function errorsAtFn(
  browser: Record<string, string[]>,
  diagnostics: readonly { path: string; message: string; severity: string }[],
) {
  return (pointer: string): string[] => [
    ...Object.entries(browser)
      .filter(([key]) => key === pointer || key.startsWith(`${pointer}/`))
      .flatMap(([, messages]) => messages),
    ...diagnostics
      .filter((d) => d.severity !== 'info' && (d.path === pointer || d.path.startsWith(`${pointer}/`)))
      .map((d) => d.message),
  ];
}
